import { useQuery } from '@tanstack/react-query';

import { fetchSubscription } from '@/features/billing/api/billing';
import { LOCKED_SUBSCRIBE_PATH } from '@/features/billing/lib/billingLock';
import { SUBSCRIBE_PATH } from '@/features/billing/lib/planSelection';
import { isSettled } from '@/features/billing/lib/subscriptionState';

import { useAuth } from './useAuth';

/**
 * Where the signed-in account works, and what to call it in navigation.
 *
 * The marketing header shows this in place of "Log in" and "Start free trial",
 * so a merchant who has already registered is never asked to register again.
 * It answers the same question `ProtectedRoute` answers when it bounces a
 * caller, and deliberately in the same order: a link that disagreed with its
 * destination's own guard would land the merchant somewhere they did not ask
 * for, which is the bug this replaces rather than a different shape of it.
 *
 * `/subscribe` rather than `/app` while the plan is unsettled, because that
 * screen is the last step of signing up and a merchant who left it half
 * finished should come back to it. Only a positive "no live subscription" sends
 * them there: a billing call still in flight, refused, or one this member may
 * not make at all means the dashboard, since guessing "unsubscribed" would walk
 * a paying merchant into checkout a second time.
 */

export interface Workspace {
  /** Whether there is a session at all - what the header switches on. */
  signedIn: boolean;
  /** The account's own landing page. */
  to: string;
  /** The label for the menu item that links to it. */
  label: string;
}

function landingPage({
  mustChangePassword,
  isPlatformStaff,
  isPartner,
  hasBusiness,
  locked,
  unsettled,
}: {
  mustChangePassword: boolean;
  isPlatformStaff: boolean;
  isPartner: boolean;
  hasBusiness: boolean;
  locked: boolean;
  unsettled: boolean;
}): { to: string; label: string } {
  // First, because the API refuses everything else while it holds.
  if (mustChangePassword) return { to: '/change-password', label: 'My account' };
  // Neither of these accounts owns a business, and neither ever will.
  if (isPlatformStaff) return { to: '/platform', label: 'Platform' };
  if (isPartner) return { to: '/partner', label: 'Partner portal' };
  // Sign-up, interrupted. The business comes before the plan, because a
  // subscription belongs to one.
  if (!hasBusiness) return { to: '/onboarding', label: 'Finish setting up' };
  // A lapsed plan: the dashboard would only send them to pay, so the link
  // says so - and lands on the subscribe screen wearing the lock banner.
  if (locked) return { to: LOCKED_SUBSCRIBE_PATH, label: 'Renew your plan' };
  if (unsettled) return { to: SUBSCRIBE_PATH, label: 'Finish setting up' };
  return { to: '/app', label: 'My account' };
}

export function useWorkspace(): Workspace {
  const { status, hasBusiness, isPlatformStaff, isPartner, mustChangePassword, billingLock, can } =
    useAuth();

  const signedIn = status === 'authenticated';
  // Only a merchant with a business can hold a subscription, and only a member
  // the API lets read billing can be told about one. Everybody else's
  // destination is settled without asking, so nothing is fetched for them - a
  // marketing page has no business making a call it cannot use.
  const planMatters =
    signedIn &&
    hasBusiness &&
    !isPlatformStaff &&
    !isPartner &&
    !mustChangePassword &&
    can('BILLING_READ');

  // The key the dashboard and the billing page already use, so a merchant
  // arriving from either brings the answer with them rather than asking twice.
  const subscription = useQuery({
    queryKey: ['subscription'],
    queryFn: fetchSubscription,
    enabled: planMatters,
    staleTime: 60_000,
  });

  return {
    signedIn,
    ...landingPage({
      mustChangePassword,
      isPlatformStaff,
      isPartner,
      hasBusiness,
      locked: billingLock !== null,
      unsettled:
        planMatters && subscription.data !== undefined && !isSettled(subscription.data),
    }),
  };
}
